import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import './RoleSelection.css';
import cacheStorage from '../utils/cacheStorage';

export default function RoleSelection() {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const accessToken = cacheStorage.getItem('access_token');
        const idToken = cacheStorage.getItem('id_token');

        if (accessToken && idToken) {
            try {
                const decodedIdToken = jwtDecode(idToken);
                const primaryRole = decodedIdToken['https://SmartCargo/roles']?.[0];
                
                const isCourier = primaryRole === 'courier';
                const isAdmin = primaryRole === 'admin' || primaryRole === 'master admin';

                if (isCourier) {
                    navigate('/courier', { replace: true });
                } else if (isAdmin) {
                    navigate('/admin', { replace: true });
                }
            } catch (err) {
                console.error("Failed to decode existing token:", err);
            }
        }
    }, [navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Fetching variables from the .env file!
        const domain = process.env.REACT_APP_AUTH0_DOMAIN;
        const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
        const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

        try {
            const response = await fetch(`https://${domain}/oauth/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    grant_type: 'password',
                    username: username,
                    password: password,
                    audience: audience,
                    client_id: clientId,
                    scope: 'openid profile email'
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error_description || data.error || 'Login failed');
            }

            const decodedIdToken = jwtDecode(data.id_token);
            const primaryRole = decodedIdToken['https://SmartCargo/roles']?.[0];
            const pictureUrl = decodedIdToken.picture || null;

            const isCourier = primaryRole === 'courier';
            const isAdmin = primaryRole === 'admin' || primaryRole === 'master admin';

            await cacheStorage.setItem('access_token', data.access_token);
            await cacheStorage.setItem('id_token', data.id_token);

            if (pictureUrl) {
                await cacheStorage.setItem('user_picture', pictureUrl);
            } else {
                await cacheStorage.removeItem('user_picture');
            }

            const userPhone =
                decodedIdToken["https://SmartCargo/phone"] ||
                decodedIdToken["https://smartcargo.com/phone"] ||
                decodedIdToken.phone_number ||
                decodedIdToken.phone ||
                decodedIdToken.user_metadata?.phone ||
                "";

            if (userPhone) {
                await cacheStorage.setItem("user_phone", userPhone);
            } else {
                await cacheStorage.removeItem("user_phone");
            }

            if (isCourier) {
                navigate('/courier');
            } else if (isAdmin) {
                navigate('/admin');
            } else {
                setError('No valid role found for this user.');
            }

        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="role-container">
            <h1 className="role-title">Login</h1>
            <p className="role-subtitle">Please log in to access your dashboard</p>

            <div className="cards-wrapper" style={{ justifyContent: 'center' }}>
                <div className="role-card" style={{ cursor: 'default' }}>
                    <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                        {error && <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}
                        <input
                            type="text"
                            placeholder="Username or Email"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            style={{ padding: '0.75rem', borderRadius: '4px', border: '1px solid #ccc', fontSize: '1rem' }}
                        />
                        <div style={{ position: 'relative', width: '100%' }}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                style={{ padding: '0.75rem', paddingRight: '2.75rem', borderRadius: '4px', border: '1px solid #ccc', fontSize: '1rem', width: '100%', boxSizing: 'border-box' }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(prev => !prev)}
                                style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#6b7280'
                                }}
                            >
                                {showPassword ? (
                                    /* Eye with slash — password is visible, click to hide */
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                        <line x1="1" y1="1" x2="23" y2="23"/>
                                    </svg>
                                ) : (
                                    /* Open eye — password is hidden, click to show */
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                padding: '0.75rem',
                                borderRadius: '4px',
                                backgroundColor: '#4f46e5',
                                color: 'white',
                                border: 'none',
                                fontSize: '1rem',
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}